import { UseFormSetValue } from 'react-hook-form';
import { ISearchByMenuItems } from '../../models/purchase-history';
import { addPhoneHyphens } from './form-utils';

export const searchByMenuItems = (
  setValue: UseFormSetValue<{
    searchByTextfield: string;
  }>,
): ISearchByMenuItems[] => [
  {
    name: 'Item Description',
    value: 'itemDescription',
    placeholder: 'Beginning with...',
    options: {
      minLength: {
        value: 1,
        message: 'Please enter an item description.',
      },
      required: true,
    },
    errorMessages: {
      minLength: 'Please enter an item description.',
    },
  },
  {
    name: 'PO/Job Name',
    value: 'POJobName',
    placeholder: 'Beginning with...',
    options: {},
  },
  {
    name: 'Buyer Name',
    value: 'buyerName',
    placeholder: 'Enter First Name or Last Name',
  },
  {
    name: 'Buyer ID',
    value: 'buyerId',
    placeholder: 'Enter 5-digit Buyer ID',
    options: { maxLength: 5, pattern: /\d/g },
    errorMessages: {
      maxLength: 'Please enter a 5 digit number.',
      pattern: 'Please use only numerical characters',
    },
  },
  {
    name: 'Store Number',
    value: 'storeNumber',
    placeholder: '----',
    options: { maxLength: 4, pattern: /\d/g },
    errorMessages: {
      maxLength: 'Please enter 4 digits.',
      pattern: 'Please use only numerical characters',
    },
  },
  {
    name: 'Order Origin',
    value: 'storeName',
    placeholder: 'Containing...',
    options: { minLength: 4, maxLength: 32 },
    errorMessages: {
      minLength: 'Please enter a value between 4 and 32 characters.',
      maxLength: 'Please enter a value between 4 and 32 characters.',
    },
  },
  {
    name: 'SKU Number',
    value: 'SKUNumber',
    placeholder: '',
    options: {
      minLength: {
        value: 1,
        message: 'Please enter a valid SKU number.',
      },
      maxLength: 45,
    },
    errorMessages: {
      minLength: 'Please enter a valid SKU number.',
      maxLength: 'Please enter a valid SKU number.',
    },
  },
  {
    name: 'Department Number',
    value: 'departmentNumber',
    placeholder: '',
    options: {
      minLength: {
        value: 1,
        message: 'Please enter a valid 2 digit department number.',
      },
      maxLength: 2,
      pattern: /\d/g,
    },
    errorMessages: {
      minLength: 'Please enter a valid 2 digit department number.',
      maxLength: 'Please enter a valid 2 digit department number.',
      pattern: 'Please enter a valid 2 digit department number.',
    },
  },
  {
    name: 'Total Amount',
    value: 'totalAmount',
    placeholder: 'Exact amount with cents',
    options: {
      minLength: {
        value: 1,
        message: 'Please enter a valid total amount.',
      },
      pattern: /^(?!.*#)[+-]?\$?[0-9]{1,3}(?:,?[0-9]{3})*(?:\.[0-9]{1,2})?$/g,
      validate: (value: number) => value <= 999999.99,
    },
    errorMessages: {
      minLength: 'Please enter a valid total amount.',
      pattern: 'Please enter a valid total amount.',
      validate: 'Please enter a total less than $999,999.99.',
    },
  },
  {
    name: 'Order Number',
    value: 'orderNumber',
    placeholder: 'Beginning with W, H or C',
    options: { pattern: /^[WHCwhc]\w+?[\d-]+/ },
    errorMessages: {
      pattern: 'Please enter a valid order number.',
    },
  },
  {
    name: 'Credit Card (Last 4 Digits)',
    value: 'creditDebitCard',
    placeholder: '----',
    options: { maxLength: 4, pattern: /\d/g },
    errorMessages: {
      maxLength: 'Please enter a 4 digit number.',
      pattern: 'Please enter only numerical characters',
    },
  },
  {
    name: 'Reloadable Card (Digits 13-16)',
    value: 'proXtrareloadableCard',
    placeholder: '----',
    options: { maxLength: 4, pattern: /\d/g },
    errorMessages: {
      maxLength: 'Please enter a 4 digit number.',
      pattern: 'Please enter only numerical characters',
    },
  },
  {
    name: 'Gift Card (Digits 13-16)',
    value: 'giftCard',
    placeholder: '----',
    options: { maxLength: 4, pattern: /\d/g },
    errorMessages: {
      maxLength: 'Please enter a 4 digit number.',
      pattern: 'Please enter only numerical characters',
    },
  },
  {
    name: 'Checking Account (Last 4 Digits)',
    value: 'checkingAccount',
    placeholder: '----',
    options: { maxLength: 4, pattern: /\d/g },
    errorMessages: {
      maxLength: 'Please enter a 4 digit number.',
      pattern: 'Please enter only numerical characters',
    },
  },
  {
    name: 'THD Keytag',
    value: 'THDKeyTag',
    placeholder: '---- ---- ---- ----',
    options: { maxLength: 12, validate: (value: number) => value !== 12 },

    errorMessages: {
      maxLength: 'Please enter a 12 digit number.',
      validate: 'Please enter a 12 digit number',
    },
  },
  {
    name: 'Phone',
    value: 'phoneNumber',
    placeholder: '--- --- ----',
    options: {
      pattern: /^(\+\d{1,2}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/,
      onChange: (event: { target: { value: string } }) =>
        setValue(
          'searchByTextfield',
          addPhoneHyphens(event.target.value.toUpperCase()),
        ),
      onBlur: (event: { target: { value: string } }) =>
        setValue(
          'searchByTextfield',
          addPhoneHyphens(event.target.value.toUpperCase()),
        ),
    },
    errorMessages: {
      pattern: 'Please enter a 10 digit number.',
    },
  },
  {
    name: 'PayPal',
    value: 'paypal',
  },
  {
    name: 'Invoice Number',
    value: 'invoiceNumber',
    placeholder: 'Enter Invoice Number',
    options: {
      pattern: /\d/g,
    },
    errorMessages: {
      pattern: 'Please enter a invoice number.',
    },
  },
];

export const requestFormatter: {
  [key: string]: (value: string) => { [key: string]: string }[];
} = {
  itemDescription: (value: string) => [{ itemDescription: value }],
  POJobName: (value: string) => [{ POJobName: value }],
  storeNumber: (value: string) => [{ storeNumber: value }],
  storeName: (value: string) => [{ storeName: value }],
  SKUNumber: (value: string) => [{ SKUNumber: value }],
  departmentNumber: (value: string) => [{ departmentNumber: value }],
  totalAmount: (value: string) => [{ totalAmount: value }],
  orderNumber: (value: string) => [{ orderNumber: value }],
  creditDebitCard: (value: string) => [{ creditDebitCard: value }],
  proXtrareloadableCard: (value: string) => [{ proXtrareloadableCard: value }],
  giftCard: (value: string) => [{ giftCard: value }],
  checkingAccount: (value: string) => [{ checkingAccount: value }],
  THDKeyTag: (value: string) => [{ THDKeyTag: value }],
  phoneNumber: (value: string) => [{ phoneNumber: value }],
  buyerName: (value: string) => [{ buyerName: value }],
  buyerId: (value: string) => [{ buyerId: value }],
  paypal: () => [{ PayPal: '' }],
  invoiceNumber: (value: string) => [{ invoiceNumber: value }],
};
